import {ServiceRegistry} from "../../platform/ServiceRegistry";
import {undoManager} from "../common/UndoManager";
import {HOTKEYS_SCOPE, setScope} from "../../utils/hotkeys";
import {getRegisteredAppState} from "@stores/appStateRegistry";

const AUTH_PAGES = new Set(['sign-in', 'sign-up', 'reset-password']);

let unauthorizedSessionPromise: Promise<void> | null = null;

const normalizeBasePath = (basePath: string | undefined) => {
    if (!basePath || basePath === '/') {
        return '';
    }

    return basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
};

export const getUnauthorizedSignInUrl = (
    pathname: string,
    basePath: string = import.meta.env.VITE_BASE_PATH,
) => {
    const normalizedBasePath = normalizeBasePath(basePath);
    const pathnameWithoutBasePath = normalizedBasePath && pathname.startsWith(normalizedBasePath)
        ? pathname.slice(normalizedBasePath.length) || '/'
        : pathname;

    const pathSegments = pathnameWithoutBasePath.split('/').filter(Boolean);
    const teamSlug = pathSegments[0] && !AUTH_PAGES.has(pathSegments[0]) ? pathSegments[0] : undefined;
    const signInPath = teamSlug ? `/${teamSlug}/sign-in` : '/sign-in';

    return `${normalizedBasePath}${signInPath}`;
};

const clearAuthStorage = () => {
    try {
        Object.keys(localStorage).forEach((key) => {
            if (key.includes('CognitoIdentityServiceProvider')) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.warn('[Auth] Failed to clear Cognito local storage during unauthorized handling', error);
    }

    try {
        sessionStorage.clear();
    } catch (error) {
        console.warn('[Auth] Failed to clear session storage during unauthorized handling', error);
    }
};

export const handleUnauthorizedSession = async (reason?: unknown): Promise<void> => {
    if (unauthorizedSessionPromise) {
        return unauthorizedSessionPromise;
    }

    unauthorizedSessionPromise = (async () => {
        if (reason) {
            console.warn('[Auth] Unauthorized session detected', reason);
        }

        const appState = getRegisteredAppState();
        let serviceRegistry: ServiceRegistry | null = null;

        try {
            serviceRegistry = ServiceRegistry.getInstance();
        } catch (error) {
            console.warn('[Auth] ServiceRegistry is not available during unauthorized handling', error);
        }

        const authService = serviceRegistry?.getAuthService();
        const dataClientService = serviceRegistry?.getDataClientService();

        if (dataClientService) {
            try {
                await dataClientService.disconnectAllWsClients();
            } catch (error) {
                console.warn('[Auth] Failed to disconnect WS clients during unauthorized handling', error);
            }
        }

        if (authService) {
            try {
                await authService.signOut();
            } catch (error) {
                console.warn('[Auth] Failed to sign out during unauthorized handling', error);
            }
        }

        try {
            appState?.resetState();
        } catch (error) {
            console.warn('[Auth] Failed to reset app state during unauthorized handling', error);
        }

        undoManager.resetState();
        setScope(HOTKEYS_SCOPE.UNAUTHORIZED);
        clearAuthStorage();

        window.location.replace(getUnauthorizedSignInUrl(window.location.pathname));
    })();

    return unauthorizedSessionPromise;
};
